import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * ADMIN-ONLY theme system.
 *
 * Strictly isolated from the customer/global theme (`useTheme` / `wg-theme`).
 * - Storage key: `bns-admin-theme` (never touches `wg-theme`).
 * - DOM marker: `data-admin-theme` on <html> (customer CSS never reads it).
 * - Default: `dark` (futuristic reference), persisted across refreshes.
 * - Applies to /admin/* routes only; customer pages never consume this context.
 */
export type AdminTheme = 'light' | 'dark'

interface AdminThemeContextType {
  adminTheme: AdminTheme
  setAdminTheme: (t: AdminTheme) => void
  toggleAdminTheme: () => void
}

const AdminThemeContext = createContext<AdminThemeContextType | undefined>(undefined)

export { AdminThemeContext }

const STORAGE_KEY = 'bns-admin-theme'

function getInitialAdminTheme(): AdminTheme {
  if (typeof window === 'undefined') return 'dark'
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    // ignore storage errors (private mode) -> fall through to default
  }
  return 'dark'
}

function applyAdminTheme(theme: AdminTheme) {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.adminTheme = theme
}

export function AdminThemeProvider({ children }: { children: ReactNode }) {
  const [adminTheme, setAdminThemeState] = useState<AdminTheme>(() => getInitialAdminTheme())

  useEffect(() => {
    applyAdminTheme(adminTheme)
    try {
      window.localStorage.setItem(STORAGE_KEY, adminTheme)
    } catch {
      // ignore storage errors
    }
  }, [adminTheme])

  const setAdminTheme = (t: AdminTheme) => setAdminThemeState(t)
  const toggleAdminTheme = () => setAdminThemeState((t) => (t === 'light' ? 'dark' : 'light'))

  return (
    <AdminThemeContext.Provider value={{ adminTheme, setAdminTheme, toggleAdminTheme }}>
      {children}
    </AdminThemeContext.Provider>
  )
}

export function useAdminTheme() {
  const ctx = useContext(AdminThemeContext)
  if (!ctx) throw new Error('useAdminTheme must be used within an AdminThemeProvider')
  return ctx
}
